import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';
import type { CheckoutState, PaymentMethod } from './checkoutTypes';
import type { MomoNetwork } from './utils/ghanaMomo';
import { ParentDashboardApi } from '../ParentDashboard/ParentDashboardApi';

export interface PayFeesRequest {
  amount: number;
  studentNumber: string;
  discounts: { policyId: string; amount: number; method: string }[];
  subscriptions?: { id: number; amount: number }[];
  phoneNumber?: string;
  network?: MomoNetwork;
  paymentMethod?: PaymentMethod;
  startAgain?: boolean;
}

export type PayFeesResponse = Partial<CheckoutState> & {
  paymentUrl?: string | null;
};

export interface ParentCheckoutRef {
  studentNumber: string;
  reference: string;
}

type Dispatch = (action: unknown) => unknown;

/**
 * Balances live in ParentDashboardApi (a separate slice, so its tags can't be
 * listed in `invalidatesTags` here). Once a payment call succeeds, invalidate
 * them so Today and Pay show the new totals without waiting for the next poll
 * or a full-page reload.
 */
const refreshBalancesOnSuccess = async <T>(
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: Dispatch; queryFulfilled: Promise<{ data: T }> },
  isSuccess: (data: T) => boolean = () => true
) => {
  try {
    const { data } = await queryFulfilled;
    if (isSuccess(data)) {
      dispatch(ParentDashboardApi.util.invalidateTags(['Dashboard']));
    }
  } catch {
    // The failed call surfaces its own error; there is nothing to refresh.
  }
};

export const ParentPaymentApi = createApi({
  reducerPath: 'ParentPaymentApi',
  baseQuery,
  endpoints: builder => ({
    payFees: builder.mutation<PayFeesResponse, PayFeesRequest>({
      query: ({
        amount,
        studentNumber,
        discounts,
        subscriptions,
        phoneNumber,
        network,
        paymentMethod,
        startAgain,
      }) => {
        return {
          url: `/parent/fees/${studentNumber}/pay`,
          method: 'POST',
          body: {
            amount,
            discounts,
            subscriptions,
            ...(phoneNumber ? { phoneNumber } : {}),
            ...(network ? { network } : {}),
            ...(paymentMethod ? { paymentMethod } : {}),
            ...(startAgain ? { startAgain } : {}),
          },
        };
      },
      onQueryStarted: (_arg, api) => refreshBalancesOnSuccess(api),
    }),
    confirmParentCheckoutOtp: builder.mutation<
      CheckoutState,
      ParentCheckoutRef & { otp: string }
    >({
      query: ({ studentNumber, reference, otp }) => ({
        url: `/parent/fees/${studentNumber}/pay/${reference}/confirm-otp`,
        method: 'POST',
        body: { otp },
      }),
      onQueryStarted: (_arg, api) => refreshBalancesOnSuccess(api),
    }),
    getParentCheckoutStatus: builder.query<CheckoutState, ParentCheckoutRef>({
      query: ({ studentNumber, reference }) => ({
        url: `/parent/fees/${studentNumber}/pay/${reference}/status`,
      }),
      keepUnusedDataFor: 0,
      // A person is waiting on "did my money move?": never held by the outage hold.
      extraOptions: { bypassOutageHold: true },
      onQueryStarted: (_arg, api) =>
        refreshBalancesOnSuccess(api, data => data.checkoutStep === 'paid'),
    }),
  }),
});

export const {
  usePayFeesMutation,
  useConfirmParentCheckoutOtpMutation,
  useLazyGetParentCheckoutStatusQuery,
} = ParentPaymentApi;
